import { useEffect, useState, type ReactElement } from 'react';
import type { Gym, User } from '@acrostic/model';
import { SourceProvider, useAsync, useSource } from './data/SourceProvider.js';
import { ToastHost } from './components/Toast.js';
import { SignIn } from './screens/SignIn.js';
import { Dashboard, HomeCoach, HomeManager, HomeSales } from './screens/Home.js';
import { Members } from './screens/Members.js';
import { MemberRecord } from './screens/MemberRecord.js';
import { Leads } from './screens/Leads.js';
import { LeadRecord } from './screens/LeadRecord.js';
import { Classes } from './screens/Classes.js';
import { Segments } from './screens/Segments.js';
import { Campaigns } from './screens/Campaigns.js';
import { Approvals } from './screens/Approvals.js';
import { Trainers } from './screens/Trainers.js';
import { Accounts } from './screens/Accounts.js';
import { Settings } from './screens/Settings.js';
import { Coach } from './screens/Coach.js';
import { Worth } from './screens/Worth.js';
import { permissionsOf } from '@acrostic/model';
import { setGymToday } from './lib/today.js';
import { homeFor, navFor, seatLabel, seatsOf } from './seats.js';
import { go, useRoute } from './routes.js';
import { Tour } from './tour/Tour.js';
import { TOURS } from './tour/scripts.js';

/* What every screen is handed. Who is looking decides what a screen shows - a
   Support Coach's board opens on her own leads - so the session goes in rather than
   each screen reaching for it and each one deciding differently. */
export interface ScreenProps { user: User; gym: Gym }

/* Every screen the app can show. A route with no entry here is a screen that has not
   been built yet, and the shell says so plainly rather than rendering blank. */
const SCREENS: Record<string, (p: ScreenProps) => ReactElement> = {
  'home-sales': HomeSales,
  'home-coach': HomeCoach,
  'home-manager': HomeManager,
  dashboard: Dashboard,
  members: Members,
  classes: Classes,
  segments: Segments,
  campaigns: Campaigns,
  approvals: Approvals,
  trainers: Trainers,
  accounts: Accounts,
  settings: Settings,
  coach: Coach,
  worth: Worth,
  leads: ({ user }) => (
    <Leads me={{
      id: user.id, name: user.name,
      /* A book of one's own is a SUPPORT COACH thing. A manager and an owner see the
         whole board because that is the job; a coach has no lead book at all. */
      hasLeadBook: user.roles.includes('sales'),
    }} />
  ),
};

const TITLES: Record<string, string> = {
  'home-sales': 'Support Coach Console',
  'home-coach': 'Coach Console',
  'home-manager': 'My Day',
  dashboard: 'Dashboard',
  members: 'Members',
  'member-detail': 'Member record',
  leads: 'Leads',
  'lead-detail': 'Lead',
  classes: 'Classes',
  segments: 'Segments',
  campaigns: 'Campaigns',
  approvals: 'Approvals',
  trainers: 'Coaches & Trainers',
  accounts: 'Staff accounts',
  settings: 'Settings',
  coach: 'My PT Clients',
  worth: 'Worth & value',
};

function NotBuilt({ name }: { name: string }) {
  return (
    <section className="panel">
      <h2 className="t-heading-h3">{name}</h2>
      <p className="t-body-medium muted" style={{ marginTop: 8 }}>
        Not rebuilt yet. It exists in the current demo and is on the list.
      </p>
    </section>
  );
}

/* WHICH SEATS HAVE BEEN WALKED THROUGH, for this page load only.
 *
 * Deliberately not persisted. Every fresh open of the demo offers the walkthrough
 * again, because the next person to open it is usually a different person; skipping
 * still holds for the rest of that visit. Module scope rather than state, so signing
 * out and back in as the same seat does not re-offer it mid-visit. */
const walked = new Set<string>();

function Shell() {
  const source = useSource();
  const [epoch, setEpoch] = useState(0);
  const session = useAsync(() => source.getSession(), [source, epoch]);
  const route = useRoute();
  const [tour, setTour] = useState<string | null>(null);

  /* Offered on arrival at a seat that has one and has not seen it. In an effect, not
     in render: starting a tour is a navigation, and a render that navigates is a
     render that fights the router. */
  const seat = session.data ? homeFor(session.data.user.roles) : null;
  const seatKey = session.data?.user.roles[0] ?? null;
  useEffect(() => {
    if (!seatKey || !TOURS[seatKey] || walked.has(seatKey)) return;
    walked.add(seatKey);
    setTour(seatKey);
  }, [seatKey]);
  void seat;

  if (session.loading) return null;
  if (!session.data) return <SignIn onIn={() => setEpoch((n) => n + 1)} />;

  const { user, gym } = session.data;
  /* THE GYM'S DAY, not the laptop's. Set before anything renders a relative date:
     every "N days ago" in the app goes through fmt, and fmt asks this. */
  setGymToday(session.data.today);
  const roles = user.roles;
  /* Resolved the same way the server resolves it, from the same function, so the
     sidebar and the routes cannot disagree about what this person may do. */
  const mine = permissionsOf(roles, user.permissions ?? null);
  const home = homeFor(roles);
  const name = route.name === '__home__' ? home : route.name;
  const Screen = SCREENS[name];
  const title = TITLES[name] ?? name.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());
  /* A record is reached from its list, so the list stays lit in the nav: a member
     record with nothing current in the sidebar reads as having left the section. */
  const lit = name === 'member-detail' ? 'members' : name === 'lead-detail' ? 'leads' : name;

  return (
    <div className="shell">
      {/* data-surface="dark" is a SCOPE, not a theme. The design file resolves the
          same token names to inverted values on the sidebar, so packages/tokens
          re-declares them under this attribute and everything inside reads them
          without knowing it is on a dark surface. Take the attribute off and the
          sidebar renders black on white, because every colour in here is a token. */}
      <aside className="sidebar" data-surface="dark">
        <div className="brand t-heading-h3">Acrostic</div>
        <nav>
          {navFor(roles, mine).map((item, i) => item.header
            ? <div className="navhead t-label-xsmall" key={'h' + i}>{item.label}</div>
            : (
              <a
                key={item.name}
                href={'#/' + item.name}
                {...(item.name === lit ? { 'aria-current': 'page' as const } : {})}
              >{item.label}</a>
            ))}
        </nav>
        <div style={{ flex: 1 }} />
        {/* SIGNING OUT BELONGS BESIDE WHO YOU ARE, not in the top right opposite the
            screen's own title. It is a fact about the account, and the account is
            named here. It also stops the top bar being a place where one destructive
            control sits next to the walkthrough. */}
        <div className="t-caption whoami">
          <div className="who">
            <div className="gym">{gym.name}</div>
            <div className="name">{user.name}</div>
            {/* `seatline`, NOT `seat`. `.seat` is the sign-in card's button, and
                this picked up its white background, border and 14px of padding the
                moment it was added. The codebase renamed the identity block out of
                that collision once already; this is the same trap one level down. */}
            <div className="muted seatline">{seatsOf(roles).map(seatLabel).join(' · ')}</div>
          </div>
          <button
            type="button" className="signout t-label-small"
            onClick={() => void source.logout().then(() => { go('/'); setEpoch((n) => n + 1); })}
          >Sign out</button>
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <h1 className="t-heading-h2">{title}</h1>
          <span className="spacer" />
          {TOURS[roles[0] ?? ''] && (
            <button type="button" className="pill walkthrough t-label-small"
              onClick={() => setTour(roles[0] ?? null)}>Walkthrough</button>
          )}
        </div>
        <div className="content">
          {/* Which screen is on. The walkthrough waits for this to change rather
              than for the node to be replaced: React keeps .page across a route and
              re-renders its children, so "wait for a new .page" waited forever. */}
          <div className="page" data-screen={name}>
            {route.name === 'member-detail' && route.id ? <MemberRecord id={route.id} />
              : route.name === 'lead-detail' && route.id ? <LeadRecord id={route.id} />
              : Screen ? <Screen user={user} gym={gym} />
              : <NotBuilt name={title} />}
          </div>
        </div>
      </main>

      {tour && <Tour seat={tour} onEnd={() => setTour(null)} />}
    </div>
  );
}

export function App() {
  return (
    <SourceProvider>
      <ToastHost><Shell /></ToastHost>
    </SourceProvider>
  );
}
